/**
 * Transition bridge: lets a not-yet-native mutation feed the overlay.
 *
 * A native connection mutation puts its operation straight into the mutation cache (see
 * `useConnectionMutation`). A mutation whose server write still runs through Relay's
 * `commitMutation` instead **dispatches** `optimistic`/`completed`/`error` events carrying
 * its `ConnectionOperation`; a single `useConnectionMutationBridge` per connection mirrors
 * each in-flight operation into the mutation cache. Because the coupling lives at the
 * mutation (it emits) and one bridge (it mirrors), every call site feeds the overlay with
 * no per-call-site wiring.
 *
 * Delete this module once every connection mutation is a native `useMutation`.
 */
import {useMutation} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useRef} from 'react'

import {
  type ConnectionOperation,
  connectionPathKey,
  DOCUMENT_FIELDS_PATH,
  type EntityOperation,
  isEntityOperation,
} from './connection-operations'
import {notifySettlement} from './settlement-registry'
import {
  type ConnectionMutationVariables,
  connectionMutationKey,
  ENTITY_FIELDS_OWNER,
  entityFieldsMutationKey,
} from './pending-operations'

const CONNECTION_MUTATION_EVENT = 'connection-mutation'

/**
 * One phase of a bridged (non-native) connection mutation. `mutationId` correlates the
 * `optimistic` → `completed`/`error` phases of a single invocation.
 */
export type ConnectionMutationEventDetail =
  | {phase: 'optimistic'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'completed'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'error'; mutationId: string; error: Error}

class ConnectionMutationEvent extends Event {
  readonly detail: ConnectionMutationEventDetail
  constructor(detail: ConnectionMutationEventDetail) {
    super(CONNECTION_MUTATION_EVENT)
    this.detail = detail
  }
}

/** The bus bridged mutations dispatch on and `useConnectionMutationBridge` reads. */
const connectionMutationEventTarget: EventTarget = new EventTarget()

let idCounter = 0
/** A fresh correlation id for one bridged mutation invocation. */
export function createConnectionMutationId(): string {
  return `conn-mut-${Date.now().toString(36)}-${(idCounter++).toString(36)}`
}

/**
 * Dispatch one phase of a bridged connection mutation. Producers call this from
 * their Relay `commitMutation` optimistic/onCompleted/onError callbacks so that
 * every call site of the mutation feeds the overlay.
 */
export function dispatchConnectionMutation(detail: ConnectionMutationEventDetail): void {
  connectionMutationEventTarget.dispatchEvent(new ConnectionMutationEvent(detail))
}

// Align an operation's own `connectionPath` to this bridge's key path before it enters the
// mutation cache (optimistic) or a settle fold (completed). `applyOperation(s)` locates the
// connection from the operation's own path, so a producer that emitted a mismatched path could
// otherwise mutate a sibling connection. `setFields` carries no connection path. Mirrors the
// same invariant `useConnectionMutation` enforces on its native producers.
function withKeyAlignedPath(operation: ConnectionOperation, connectionPath: string[]): ConnectionOperation {
  return isEntityOperation(operation) ? operation : {...operation, connectionPath}
}

type Deferred = {promise: Promise<void>; resolve: () => void; reject: (reason?: unknown) => void}
function createDeferred(): Deferred {
  let resolve!: () => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })
  return {promise, resolve, reject}
}

type BridgeVariables = ConnectionMutationVariables & {mutationId: string; settled: Promise<void>}

// A settler plus the completion callback and the connection path captured at optimistic time,
// so a late terminal event folds into the connection the mutation *started* against — not
// whatever `onCompleted`/`connectionPath` this bridge instance has after e.g. navigating to
// another issue or being re-pointed at a different connection.
type Settler = Deferred & {
  onCompleted?: (
    operation: ConnectionOperation,
    operationId?: string,
    options?: {forceRevalidate?: boolean},
  ) => void | Promise<void>
  onOutstandingAbandoned?: () => void
  connectionPath: string[]
}

type KeyedPublication = {key: string; sequence: number}
type ScalarDeltaPublication = KeyedPublication & {
  field: string
  directions: Record<string, 1 | -1>
}
type BulkItemsPublication = KeyedPublication
type EntitySettler = Settler & {
  keyed?: KeyedPublication
  scalarDelta?: ScalarDeltaPublication
  bulkItems?: BulkItemsPublication
  superseded?: boolean
  terminalReceived?: boolean
}
type KeyedPublicationState = {
  latestSettledSequence: number
  active: Set<number>
  terminalQueue: Promise<void>
}
type ScalarDeltaTerminal = {
  detail: Extract<ConnectionMutationEventDetail, {phase: 'completed'}>
  settler: EntitySettler
}
type ScalarDeltaPublicationState = {
  active: Set<number>
  terminals: Map<number, ScalarDeltaTerminal>
}
type BulkItemsTerminal = {
  detail: Extract<ConnectionMutationEventDetail, {phase: 'completed'}>
  settler: EntitySettler
}
type BulkItemsPublicationState = {
  active: Set<number>
  terminals: Map<number, BulkItemsTerminal>
  draining: boolean
  needsRevalidation: boolean
}

function entityItemKey(operation: EntityOperation): string | undefined {
  if (operation.type !== 'upsertEntityFieldItem' && operation.type !== 'removeEntityFieldItem') return
  return JSON.stringify([operation.id, operation.field, operation.key])
}

function scalarDeltaPublication(operation: EntityOperation, sequence: number): ScalarDeltaPublication | undefined {
  if (operation.type === 'deltaEntityField') {
    if (operation.delta === 0) return
    const direction = operation.delta > 0 ? 1 : -1
    const directions = {['']: direction} as const
    return {
      key: JSON.stringify([operation.id, operation.field, operation.type]),
      sequence,
      field: operation.field,
      directions,
    }
  }
  if (operation.type !== 'deltaEntityFieldValues') return
  const entries = Object.entries(operation.deltas).filter(([, delta]) => delta !== 0)
  const signs = new Set(entries.map(([, delta]) => (delta > 0 ? 1 : -1)))
  if (signs.size !== 1) return
  const direction = signs.values().next().value as 1 | -1
  const directions = Object.fromEntries(entries.map(([field]) => [field, direction]))
  if (Object.keys(directions).length === 0) return
  return {
    key: JSON.stringify([operation.id, operation.field, operation.type]),
    sequence,
    field: operation.field,
    directions,
  }
}

function bulkItemsPublication(operation: EntityOperation, sequence: number): BulkItemsPublication | undefined {
  if (operation.type !== 'updateEntityFieldItems') return
  return {key: JSON.stringify([operation.id, operation.field, operation.type]), sequence}
}

function authoritativeDeltaValues(
  operation: ConnectionOperation,
  field: string,
  directions: Record<string, 1 | -1>,
): Record<string, number> | undefined {
  if (operation.type !== 'setFields') return
  const fieldValue = operation.fields[field]
  if (Object.hasOwn(directions, '')) {
    return typeof fieldValue === 'number' && Number.isFinite(fieldValue) ? {['']: fieldValue} : undefined
  }
  if (!fieldValue || typeof fieldValue !== 'object' || Array.isArray(fieldValue)) return
  const values: Record<string, number> = {}
  for (const nestedField of Object.keys(directions)) {
    const value = (fieldValue as Record<string, unknown>)[nestedField]
    if (typeof value !== 'number' || !Number.isFinite(value)) return
    values[nestedField] = value
  }
  return values
}

function cleanKeyedState(states: Map<string, KeyedPublicationState>, key: string, state: KeyedPublicationState) {
  if (state.active.size === 0) states.delete(key)
}

function cleanupKeyedPublication(settler: EntitySettler, states: Map<string, KeyedPublicationState>) {
  const keyed = settler.keyed
  if (!keyed) return
  const state = states.get(keyed.key)
  state?.active.delete(keyed.sequence)
  if (state) cleanKeyedState(states, keyed.key, state)
}

function retireOlderPending(settlers: Map<string, EntitySettler>, key: string, sequence: number) {
  for (const candidate of settlers.values()) {
    const candidateKeyed = candidate.keyed
    if (!candidateKeyed || candidateKeyed.key !== key || candidateKeyed.sequence >= sequence) continue
    candidate.superseded = true
    candidate.resolve()
  }
}

// Serializes terminal events per entity-field key so stale confirmations cannot overwrite newer values.
function handleSequencedKeyedEntityTerminal({
  detail,
  settler,
  settlers,
  states,
}: {
  detail: Exclude<ConnectionMutationEventDetail, {phase: 'optimistic'}>
  settler: EntitySettler
  settlers: Map<string, EntitySettler>
  states: Map<string, KeyedPublicationState>
}): boolean {
  if (settler.superseded) {
    settlers.delete(detail.mutationId)
    cleanupKeyedPublication(settler, states)
    return true
  }

  const keyed = settler.keyed
  const state = keyed ? states.get(keyed.key) : undefined
  const confirmedOperation =
    detail.phase === 'completed' && isEntityOperation(detail.operation) ? detail.operation : undefined
  const matchesKey = keyed && state && confirmedOperation && entityItemKey(confirmedOperation) === keyed.key
  if (!keyed || !state || (detail.phase !== 'error' && !matchesKey)) return false
  if (settler.terminalReceived) return true

  settler.terminalReceived = true
  const previousTerminal = state.terminalQueue
  state.terminalQueue = (async () => {
    await previousTerminal
    try {
      if (settler.superseded) return

      if (detail.phase === 'error') {
        settler.reject(detail.error)
        return
      }

      if (!confirmedOperation) throw new Error('keyed completion missing its entity operation')
      if (keyed.sequence < state.latestSettledSequence) {
        settler.resolve()
        return
      }

      await settler.onCompleted?.(confirmedOperation)
      state.latestSettledSequence = keyed.sequence
      retireOlderPending(settlers, keyed.key, keyed.sequence)
      settler.resolve()
    } catch (error) {
      settler.reject(error)
    } finally {
      state.active.delete(keyed.sequence)
      settlers.delete(detail.mutationId)
      cleanKeyedState(states, keyed.key, state)
    }
  })()
  return true
}

function handleScalarDeltaTerminal({
  detail,
  settler,
  settlers,
  states,
}: {
  detail: Exclude<ConnectionMutationEventDetail, {phase: 'optimistic'}>
  settler: EntitySettler
  settlers: Map<string, EntitySettler>
  states: Map<string, ScalarDeltaPublicationState>
}): boolean {
  const publication = settler.scalarDelta
  if (!publication) return false
  if (settler.terminalReceived) return true

  const state = states.get(publication.key)
  if (!state) return false
  settler.terminalReceived = true
  if (detail.phase === 'error') {
    state.active.delete(publication.sequence)
    settlers.delete(detail.mutationId)
    settler.reject(detail.error)
    if (state.active.size === 0) {
      states.delete(publication.key)
      return true
    }
    if (state.terminals.size < state.active.size) return true
  } else {
    state.terminals.set(publication.sequence, {detail, settler})
    if (state.terminals.size < state.active.size) return true
  }

  states.delete(publication.key)
  const terminals = [...state.terminals.values()]
  for (const terminal of terminals) settlers.delete(terminal.detail.mutationId)

  void (async () => {
    const directions: Record<string, 1 | -1> = {}
    let mixedDirections = false
    for (const terminal of terminals) {
      for (const [field, direction] of Object.entries(terminal.settler.scalarDelta?.directions ?? {})) {
        if (directions[field] !== undefined && directions[field] !== direction) mixedDirections = true
        directions[field] = direction
      }
    }
    const authoritative = mixedDirections
      ? terminals.reduce((selected, candidate) =>
          (candidate.settler.scalarDelta?.sequence ?? 0) > (selected.settler.scalarDelta?.sequence ?? 0)
            ? candidate
            : selected,
        )
      : terminals.reduce<(typeof terminals)[number] | undefined>((selected, candidate) => {
          const candidateValues = authoritativeDeltaValues(candidate.detail.operation, publication.field, directions)
          if (!candidateValues) return selected
          if (!selected) return candidate
          const selectedValues = authoritativeDeltaValues(selected.detail.operation, publication.field, directions)
          if (!selectedValues) return candidate
          const candidateIsNewer = Object.entries(directions).every(([field, direction]) => {
            const candidateValue = candidateValues[field]
            const selectedValue = selectedValues[field]
            if (candidateValue === undefined || selectedValue === undefined) return false
            return direction > 0 ? candidateValue >= selectedValue : candidateValue <= selectedValue
          })
          return candidateIsNewer ? candidate : selected
        }, undefined)

    try {
      if (!authoritative) {
        throw new Error(`relative delta completion missing authoritative ${publication.field}`)
      }
      await authoritative.settler.onCompleted?.(authoritative.detail.operation, authoritative.detail.mutationId, {
        forceRevalidate: mixedDirections,
      })
      for (const terminal of terminals) terminal.settler.resolve()
    } catch (error) {
      for (const terminal of terminals) terminal.settler.reject(error)
    }
  })()
  return true
}

// A capped-list operation can update several keys plus a relative count. Drain successful terminals
// in dispatch order, and keep the state while folds are in progress so later publications queue.
function handleBulkItemsTerminal({
  detail,
  settler,
  settlers,
  states,
}: {
  detail: Exclude<ConnectionMutationEventDetail, {phase: 'optimistic'}>
  settler: EntitySettler
  settlers: Map<string, EntitySettler>
  states: Map<string, BulkItemsPublicationState>
}): boolean {
  const publication = settler.bulkItems
  if (!publication) return false
  if (settler.terminalReceived) return true

  const state = states.get(publication.key)
  if (!state) return false
  const bulkState = state
  const publicationKey = publication.key
  if (
    detail.phase === 'completed' &&
    (detail.operation.type !== 'updateEntityFieldItems' ||
      JSON.stringify([detail.operation.id, detail.operation.field, detail.operation.type]) !== publication.key)
  ) {
    return false
  }

  settler.terminalReceived = true
  if (detail.phase === 'error') {
    state.active.delete(publication.sequence)
    settlers.delete(detail.mutationId)
    settler.reject(detail.error)
    if (state.active.size === 0) {
      states.delete(publication.key)
      return true
    }
  } else {
    state.terminals.set(publication.sequence, {detail, settler})
  }

  drainReadyTerminals()
  return true

  function drainReadyTerminals() {
    if (bulkState.draining || bulkState.terminals.size < bulkState.active.size) return
    bulkState.draining = true
    const terminals = [...bulkState.terminals.values()].sort(
      (left, right) => (left.settler.bulkItems?.sequence ?? 0) - (right.settler.bulkItems?.sequence ?? 0),
    )
    const forceRevalidate = bulkState.needsRevalidation
    bulkState.needsRevalidation = false
    for (const terminal of terminals) bulkState.terminals.delete(terminal.settler.bulkItems?.sequence ?? 0)

    void (async () => {
      try {
        for (const [index, terminal] of terminals.entries()) {
          await terminal.settler.onCompleted?.(terminal.detail.operation, terminal.detail.mutationId, {
            forceRevalidate: forceRevalidate && index === terminals.length - 1,
          })
          terminal.settler.resolve()
        }
      } catch (error) {
        for (const terminal of terminals) terminal.settler.reject(error)
      } finally {
        for (const terminal of terminals) {
          const sequence = terminal.settler.bulkItems?.sequence
          if (sequence !== undefined) bulkState.active.delete(sequence)
          settlers.delete(terminal.detail.mutationId)
        }
        bulkState.draining = false
        if (bulkState.active.size === 0) states.delete(publicationKey)
        else drainReadyTerminals()
      }
    })()
  }
}

/**
 * Mirrors bridged connection-mutation events for one `{ownerId, connectionPath}` into the mutation
 * cache so `useOptimisticData` overlays them.
 *
 * Mount **once per connection**, not once per document. Confirmed operations reach documents
 * through the settlement registry, so this takes no per-document configuration.
 *
 * No-op when `enabled` is false: no event listener, no mutation-cache writes.
 */
export function useConnectionMutationBridge({
  ownerId,
  connectionPath,
  enabled = true,
}: {
  ownerId: string
  connectionPath: string[]
  enabled?: boolean
}): void {
  // Settlement is not a parameter: confirmed values reach documents through the shared registry
  // `useOptimisticData` writes to. The bridge still *awaits* it, which is what keeps the overlay
  // from dropping before `base` reflects the write.
  const onCompleted = (operation: ConnectionOperation, operationId?: string, options?: {forceRevalidate?: boolean}) =>
    notifySettlement(operation, ownerId, connectionPath, operationId, options)
  const onOutstandingAbandoned = undefined
  // mutationId → settler for its terminal event, so concurrent in-flight operations on the
  // same connection don't settle each other.
  const settlersRef = useRef(new Map<string, EntitySettler>())
  const keyedSequenceRef = useRef(0)
  const keyedStatesRef = useRef(new Map<string, KeyedPublicationState>())
  const scalarDeltaSequenceRef = useRef(0)
  const scalarDeltaStatesRef = useRef(new Map<string, ScalarDeltaPublicationState>())
  const bulkItemsSequenceRef = useRef(0)
  const bulkItemsStatesRef = useRef(new Map<string, BulkItemsPublicationState>())

  const {mutate} = useMutation<void, unknown, BridgeVariables>({
    mutationKey: connectionMutationKey(ownerId, connectionPath),
    // Return the deferred captured at dispatch (carried on `variables`), not a map lookup.
    // TanStack runs this a microtask after `mutate()`; a terminal event in that gap can delete
    // the settler, and a lookup miss would resolve the mutation as successful and drop the
    // settler's rejection. Reading it from `variables` always reflects the real outcome.
    mutationFn: variables => variables.settled,
  })

  // Arrays are new each render; compare the connection by a stable, collision-free key.
  const pathKey = connectionPathKey(connectionPath)

  // `useEffectEvent` gives a stable handler that always sees the latest props
  // (ownerId, mutate, onCompleted) without re-subscribing the listener each render.
  const handleEvent = useEffectEvent((event: Event) => {
    const {detail} = event as ConnectionMutationEvent

    if (detail.phase === 'optimistic') {
      if (detail.ownerId !== ownerId || connectionPathKey(detail.connectionPath) !== pathKey) return
      // Register the settler before starting the mutation so a fast terminal event can't
      // arrive first. Capture the current `onCompleted`, `onOutstandingAbandoned` and
      // `connectionPath` so a settle (or abandonment) after this bridge re-points at another
      // connection still targets the connection the mutation *started* against.
      const deferred = createDeferred()
      const key = isEntityOperation(detail.operation) ? entityItemKey(detail.operation) : undefined
      const scalarDelta = isEntityOperation(detail.operation)
        ? scalarDeltaPublication(detail.operation, ++scalarDeltaSequenceRef.current)
        : undefined
      const bulkItems = isEntityOperation(detail.operation)
        ? bulkItemsPublication(detail.operation, ++bulkItemsSequenceRef.current)
        : undefined
      let keyed: KeyedPublication | undefined
      if (key) {
        keyed = {key, sequence: ++keyedSequenceRef.current}
        const state = keyedStatesRef.current.get(key) ?? {
          latestSettledSequence: 0,
          active: new Set<number>(),
          terminalQueue: Promise.resolve(),
        }
        state.active.add(keyed.sequence)
        keyedStatesRef.current.set(key, state)
      }
      if (scalarDelta) {
        const state = scalarDeltaStatesRef.current.get(scalarDelta.key) ?? {
          active: new Set<number>(),
          terminals: new Map<number, ScalarDeltaTerminal>(),
        }
        state.active.add(scalarDelta.sequence)
        scalarDeltaStatesRef.current.set(scalarDelta.key, state)
      }
      if (bulkItems) {
        const state = bulkItemsStatesRef.current.get(bulkItems.key) ?? {
          active: new Set<number>(),
          terminals: new Map<number, BulkItemsTerminal>(),
          draining: false,
          needsRevalidation: false,
        }
        state.active.add(bulkItems.sequence)
        if (state.active.size > 1) state.needsRevalidation = true
        bulkItemsStatesRef.current.set(bulkItems.key, state)
      }
      settlersRef.current.set(detail.mutationId, {
        ...deferred,
        onCompleted,
        onOutstandingAbandoned,
        connectionPath,
        keyed,
        scalarDelta,
        bulkItems,
      })
      // Carry the deferred promise on the variables so `mutationFn` returns this exact promise.
      // Even if a same-tick terminal event rejects it before `mutationFn` runs, returning it
      // there attaches TanStack's handler in the same microtask checkpoint — so the mutation
      // records the real outcome and the rejection is never unhandled.
      mutate({
        operation: withKeyAlignedPath(detail.operation, connectionPath),
        mutationId: detail.mutationId,
        settled: deferred.promise,
      })
      return
    }

    // completed/error: settle a mutation this bridge started.
    const settler = settlersRef.current.get(detail.mutationId)
    if (settler) {
      if (
        handleBulkItemsTerminal({
          detail,
          settler,
          settlers: settlersRef.current,
          states: bulkItemsStatesRef.current,
        })
      ) {
        return
      }
      if (
        handleScalarDeltaTerminal({
          detail,
          settler,
          settlers: settlersRef.current,
          states: scalarDeltaStatesRef.current,
        })
      ) {
        return
      }
      if (
        handleSequencedKeyedEntityTerminal({
          detail,
          settler,
          settlers: settlersRef.current,
          states: keyedStatesRef.current,
        })
      ) {
        return
      }

      settlersRef.current.delete(detail.mutationId)
      cleanupKeyedPublication(settler, keyedStatesRef.current)
      if (detail.phase === 'completed') {
        // Align to the path captured at optimistic time, not this bridge's current
        // `connectionPath` (which may have changed if it re-pointed mid-flight). Await the fold
        // before resolving: `onCompleted` is typed `void` but may return a promise (an async
        // base refresh), and resolving first would drop the optimistic operation before `base`
        // reflects the write, flashing stale data. If the fold throws or rejects, settle the
        // deferred (reject) instead — otherwise this settler is already removed from the map, so
        // the mutation would stay `pending` forever (stale overlay) and unmount cleanup couldn't
        // reject it. Rejecting drops it from the pending set; the query refetches server truth.
        void (async () => {
          try {
            await settler.onCompleted?.(withKeyAlignedPath(detail.operation, settler.connectionPath), detail.mutationId)
            settler.resolve()
          } catch (error) {
            settler.reject(error)
          }
        })()
      } else {
        // Reject with the producer's own error so mutation-cache error handlers and diagnostics
        // see the actionable Relay/server cause, not a generic bridge message.
        settler.reject(detail.error)
      }
      return
    }

    // Orphan terminal event: its `optimistic` was registered on a previous bridge instance
    // that unmounted mid-flight (navigate away then back), so there's no settler. A
    // `completed` for this connection must still fold via the current `onCompleted` —
    // otherwise a remount whose refetch raced ahead of the server keeps the stale row until
    // an unrelated refetch. `applyOperation` is idempotent, so a redundant fold is a no-op;
    // an orphan `error` carries no target and its pending operation is already gone.
    if (
      detail.phase === 'completed' &&
      detail.ownerId === ownerId &&
      connectionPathKey(detail.connectionPath) === pathKey
    ) {
      // No settler to resolve/reject here — this is an orphan completion. `onCompleted` may return
      // a promise (an async base refresh); await it in a try/catch so a rejection can't become an
      // unhandled error. If it fails the connection just reconciles on the next refetch/live update.
      void (async () => {
        try {
          await onCompleted?.(withKeyAlignedPath(detail.operation, connectionPath), detail.mutationId, {
            forceRevalidate: detail.operation.type === 'updateEntityFieldItems',
          })
        } catch {
          // Swallow: nothing to reject for an orphan, and base converges on the next server sync.
        }
      })()
    }
  })

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const outstanding = settlersRef.current
    const keyedStates = keyedStatesRef.current
    const scalarDeltaStates = scalarDeltaStatesRef.current
    const bulkItemsStates = bulkItemsStatesRef.current
    connectionMutationEventTarget.addEventListener(CONNECTION_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => {
      controller.abort()
      // On unmount the terminal event would arrive with no listener, leaving these mutations
      // `pending` forever (a later mount would replay a stale removal). Reject them now so they
      // leave the pending set.
      //
      // A terminal event dispatched while no bridge is mounted is lost (`EventTarget` doesn't
      // buffer), so those operations can't settle by fold and the consumer's base is left stale
      // (a removed row would reappear on remount). Ask the consumer to refresh each affected
      // connection's base — e.g. invalidate its query. Each abandonment callback is captured per
      // settler (like `onCompleted`), so if the bridge was re-pointed while a mutation was still
      // in flight, the connection it *started* against is refreshed too — not just the latest.
      const abandonCallbacks = new Set<() => void>()
      for (const settler of outstanding.values()) {
        if (settler.onOutstandingAbandoned) abandonCallbacks.add(settler.onOutstandingAbandoned)
        settler.reject(new Error('connection mutation bridge unmounted'))
      }
      outstanding.clear()
      keyedStates.clear()
      scalarDeltaStates.clear()
      bulkItemsStates.clear()
      for (const refreshBase of abandonCallbacks) refreshBase()
    }
  }, [enabled])
}

/**
 * Mirrors bridged **entity field** events (`DOCUMENT_FIELDS_PATH`) into the mutation cache so
 * `useOptimisticData` overlays them, and notifies the settlement registry when each one confirms.
 *
 * Mount **once for the app**, not once per document. It takes no per-document configuration, and
 * documents receive confirmed values by declaring `targets` on `useOptimisticData`. A second mount
 * is harmless but wasteful: it mirrors the same event again, and every registered document is
 * notified once per mount, so folds repeat (idempotent) and any `revalidate` refetch repeats too.
 */
export function useEntityFieldsBridge({enabled = true}: {enabled?: boolean} = {}): void {
  // Settlement is not a parameter: confirmed values reach documents through the shared registry
  // `useOptimisticData` writes to, exactly as they do for a native producer. That is what lets this
  // mount be global — one per app rather than one per document — and what lets it disappear with
  // Relay without every consumer changing.
  const onCompleted = (operation: ConnectionOperation, operationId?: string, options?: {forceRevalidate?: boolean}) => {
    if (!isEntityOperation(operation)) return
    return notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, operationId, options)
  }
  const onOutstandingAbandoned = undefined
  const settlersRef = useRef(new Map<string, EntitySettler>())
  const keyedSequenceRef = useRef(0)
  const keyedStatesRef = useRef(new Map<string, KeyedPublicationState>())
  const scalarDeltaSequenceRef = useRef(0)
  const scalarDeltaStatesRef = useRef(new Map<string, ScalarDeltaPublicationState>())
  const bulkItemsSequenceRef = useRef(0)
  const bulkItemsStatesRef = useRef(new Map<string, BulkItemsPublicationState>())

  const {mutate} = useMutation<void, unknown, BridgeVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => variables.settled,
  })

  const handleEvent = useEffectEvent((event: Event) => {
    const {detail} = event as ConnectionMutationEvent

    if (detail.phase === 'optimistic') {
      // Entity operations only; structural ones stay with the per-connection bridge, which knows
      // the connection they address.
      if (!isEntityOperation(detail.operation)) return
      if (connectionPathKey(detail.connectionPath) !== connectionPathKey(DOCUMENT_FIELDS_PATH)) return

      const deferred = createDeferred()
      const key = entityItemKey(detail.operation)
      const scalarDelta = scalarDeltaPublication(detail.operation, ++scalarDeltaSequenceRef.current)
      const bulkItems = bulkItemsPublication(detail.operation, ++bulkItemsSequenceRef.current)
      let keyed: KeyedPublication | undefined
      if (key) {
        keyed = {key, sequence: ++keyedSequenceRef.current}
        const state = keyedStatesRef.current.get(key) ?? {
          latestSettledSequence: 0,
          active: new Set<number>(),
          terminalQueue: Promise.resolve(),
        }
        state.active.add(keyed.sequence)
        keyedStatesRef.current.set(key, state)
      }
      if (scalarDelta) {
        const state = scalarDeltaStatesRef.current.get(scalarDelta.key) ?? {
          active: new Set<number>(),
          terminals: new Map<number, ScalarDeltaTerminal>(),
        }
        state.active.add(scalarDelta.sequence)
        scalarDeltaStatesRef.current.set(scalarDelta.key, state)
      }
      if (bulkItems) {
        const state = bulkItemsStatesRef.current.get(bulkItems.key) ?? {
          active: new Set<number>(),
          terminals: new Map<number, BulkItemsTerminal>(),
          draining: false,
          needsRevalidation: false,
        }
        state.active.add(bulkItems.sequence)
        if (state.active.size > 1) state.needsRevalidation = true
        bulkItemsStatesRef.current.set(bulkItems.key, state)
      }
      settlersRef.current.set(detail.mutationId, {
        ...deferred,
        onCompleted,
        onOutstandingAbandoned,
        connectionPath: DOCUMENT_FIELDS_PATH,
        keyed,
        scalarDelta,
        bulkItems,
      })
      mutate({operation: detail.operation, mutationId: detail.mutationId, settled: deferred.promise})
      return
    }

    const settler = settlersRef.current.get(detail.mutationId)
    if (!settler) {
      // Orphan completion: no optimistic phase was registered here. Two real paths reach this —
      // a producer that publishes a confirmed operation without an optimistic one (a mutation
      // committed with no optimistic payload), and a bridge that mounted after the optimistic
      // phase went out (navigate away and back mid-flight). The patch hook this replaced applied
      // completed events unconditionally, so dropping them would leave the document stale until
      // an unrelated refetch. There is nothing pending to settle, so just fold.
      //
      // Filtered on the same path as the optimistic branch above. Without it this bridge would
      // adopt the confirmed half of an operation whose optimistic half it deliberately ignored,
      // which is only currently unreachable because every `setFields` producer publishes on
      // `DOCUMENT_FIELDS_PATH`.
      if (
        detail.phase === 'completed' &&
        isEntityOperation(detail.operation) &&
        connectionPathKey(detail.connectionPath) === connectionPathKey(DOCUMENT_FIELDS_PATH)
      ) {
        void (async () => {
          try {
            await onCompleted?.(detail.operation, detail.mutationId, {
              forceRevalidate: detail.operation.type === 'updateEntityFieldItems',
            })
          } catch {
            // Nothing to reject for an orphan; base converges on the next server sync.
          }
        })()
      }
      return
    }
    if (
      handleBulkItemsTerminal({
        detail,
        settler,
        settlers: settlersRef.current,
        states: bulkItemsStatesRef.current,
      })
    ) {
      return
    }
    if (
      handleScalarDeltaTerminal({
        detail,
        settler,
        settlers: settlersRef.current,
        states: scalarDeltaStatesRef.current,
      })
    ) {
      return
    }
    if (
      handleSequencedKeyedEntityTerminal({
        detail,
        settler,
        settlers: settlersRef.current,
        states: keyedStatesRef.current,
      })
    ) {
      return
    }

    settlersRef.current.delete(detail.mutationId)
    cleanupKeyedPublication(settler, keyedStatesRef.current)

    if (detail.phase === 'error') {
      settler.reject(detail.error)
      return
    }

    void (async () => {
      try {
        await settler.onCompleted?.(detail.operation, detail.mutationId)
        settler.resolve()
      } catch (error) {
        // Leaving it pending would strand the overlay forever; rejecting drops it and the next
        // read of server truth reconciles.
        settler.reject(error)
      }
    })()
  })

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const outstanding = settlersRef.current
    const keyedStates = keyedStatesRef.current
    const scalarDeltaStates = scalarDeltaStatesRef.current
    const bulkItemsStates = bulkItemsStatesRef.current
    connectionMutationEventTarget.addEventListener(CONNECTION_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => {
      controller.abort()
      const abandonCallbacks = new Set<() => void>()
      for (const settler of outstanding.values()) {
        if (settler.onOutstandingAbandoned) abandonCallbacks.add(settler.onOutstandingAbandoned)
        settler.reject(new Error('entity fields bridge unmounted'))
      }
      outstanding.clear()
      keyedStates.clear()
      scalarDeltaStates.clear()
      bulkItemsStates.clear()
      for (const refreshBase of abandonCallbacks) refreshBase()
    }
  }, [enabled])
}

/**
 * Publishes a field edit for one entity and hands back its terminal callbacks.
 *
 * A Relay producer has four lifecycle obligations that are silent when missed: publish the
 * optimistic phase *before* `commitMutation` (the bridge subscribes synchronously, so a mutation
 * settling in the same tick would deliver its terminal first and strand the overlay), reuse one
 * correlation id, publish the server's values on success, and settle on *every* exit — including
 * `onUnsubscribe`, a setup throw, and a response too partial to fold. An unsettled operation is a
 * permanent overlay, not a stale value.
 *
 * Calling this satisfies the first two by construction and leaves the caller only `completed` and
 * `failed` to wire.
 */
type OptimisticPublication<T> = {
  completed: (confirmed: T) => void
  failed: (error: Error) => void
}

export function publishOptimisticFields(optimistic?: EntityOperation): OptimisticPublication<EntityOperation>
export function publishOptimisticFields(
  id: string,
  /**
   * Omit to publish only the confirmed operation. Some producers must not show a locally derived
   * value — `md2html` refuses to parse task-list markdown optimistically, for instance — but still
   * need to fold the server's answer. The completed operation then arrives with no optimistic phase
   * registered, which the entity bridge folds as an orphan completion.
   */
  fields?: Record<string, unknown>,
): OptimisticPublication<Record<string, unknown>>
export function publishOptimisticFields(
  idOrOperation?: string | EntityOperation,
  fields?: Record<string, unknown>,
): OptimisticPublication<Record<string, unknown>> | OptimisticPublication<EntityOperation> {
  const mutationId = createConnectionMutationId()
  const dispatch = (phase: 'optimistic' | 'completed', operation: EntityOperation) =>
    dispatchConnectionMutation({
      phase,
      mutationId,
      ownerId: operation.id,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation,
    })
  const optimistic =
    typeof idOrOperation === 'string'
      ? fields
        ? ({type: 'setFields', id: idOrOperation, fields} satisfies EntityOperation)
        : undefined
      : idOrOperation

  if (optimistic) dispatch('optimistic', optimistic)

  if (typeof idOrOperation === 'string') {
    return {
      completed: (confirmedFields: Record<string, unknown>) =>
        dispatch('completed', {type: 'setFields', id: idOrOperation, fields: confirmedFields}),
      failed: error => dispatchConnectionMutation({phase: 'error', mutationId, error}),
    }
  }

  return {
    completed: (operation: EntityOperation) => dispatch('completed', operation),
    // Harmless when nothing was published: the bridge finds no settler for this id and ignores it.
    failed: error => dispatchConnectionMutation({phase: 'error', mutationId, error}),
  }
}
